import { useCallback, useEffect, useState } from "react";
import { useAuth } from "src/hooks/use-auth";
import { Seo } from "src/components/seo";
import {
  Box,
  ButtonBase,
  Checkbox,
  CircularProgress,
  Container,
  Input,
  InputAdornment,
  Stack,
  Tooltip,
} from "@mui/material";
import { withCourierInboundProcessGuard } from "src/hocs/with-role-based-guard";
import {
  CustomButton,
  CustomFormControl,
  CustomTextField,
  CustomTooltip,
  CustomTypography,
} from "src/components/inputs";
import { useParams } from "react-router-dom";
// Redux Toolkit
import { thunks } from "src/thunks/inbound-process";
import { slice } from "src/slices/inbound-process";
import { useDispatch, useSelector } from "src/store";
import { getInboundStatusType } from "./inbound-table-settings";
import { paths } from "src/paths";
import {
  CloseOutlined,
  Delete,
  KeyboardBackspace,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { groupTypes } from "src/guards/role-based-guard";
import { MobileDatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Error404Page from "src/pages/404";
import { useDropzone } from "react-dropzone";
import { CustomDivider } from "../../../components/inputs";
import CustomCalendarIcon from "src/icons/tmw-ui/tmw-Calendar-Symbol-Option-2.png";
import moment from "moment";
import toast from "react-hot-toast";

const EditCourierDetailsPage = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const params = useParams();
  const userGroup = user?.group?.key;

  // Params
  const inboundRequestId = params.inbound_request_id;

  const {
    incotermField,
    incotermFieldLoading,
    inboundRequest,
    inboundRequestLoading,
    POItems,
    POItemsLoading,
    submitInboundRequestLoading,
    submitInboundRequestError,
    inboundRequestStatusChangeSubmitLoading,
    inboundRequestStatusChangeDraftLoading,
    inboundRequestStatusChangeApproveLoading,
  } = useSelector((state) => state.inboundProcess);

  const roundUpToNextQuarterHour = (date) => {
    const roundedDate = new Date(date);
    const minutes = date.getMinutes();
    const remainder = minutes % 15;

    if (remainder !== 0) {
      roundedDate.setMinutes(minutes + (15 - remainder));
    }

    roundedDate.setSeconds(0);
    roundedDate.setMilliseconds(0);

    return new Date(roundedDate);
  };

  // FIELDS
  const [pickupConfirmationNumber, setPickupConfirmationNumber] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [pickupDate, setPickupDate] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [carrierName, setCarrierName] = useState("");
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [barcodeLabellingRequired, setBarcodeLabellingRequired] =
    useState(false);
  const [deliveryNumber, setDeliveryNumber] = useState("");
  const [totalCartonOrPalletQTY, setTotalCartonOrPalletQTY] = useState(0);
  const [boxDimensions, setBoxDimensions] = useState("");
  const [palletDimensions, setPalletDimensions] = useState("");
  const [weight, setWeight] = useState("");
  const [pickupAddress, setPickupAddress] = useState("");
  const [openingHours, setOpeningHours] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [supplierEmailContact, setSupplierEmailContact] = useState("");
  const [contentDetails, setContentDetails] = useState("");
  const [packingListNumber, setPackingListNumber] = useState("");
  const [comments, setComments] = useState("");

  //
  // FILE SELECTS
  //

  // Waybill
  const [waybillFileName, setWaybillFileName] = useState("");
  const [waybillFileData, setWaybillFileData] = useState(null);

  // Photos
  const [courierPhotos, setCourierPhotos] = useState([]);
  const [hoveredPhoto, setHoveredPhoto] = useState("");
  const [showWrongFileTypeAlert, setShowWrongFileTypeAlert] = useState(false);
  const [selectedFilesForUpload, setSelectedFilesForUpload] = useState(null);
  const [addItemModalVisible, setAddItemModalVisible] = useState(false);
  const [photosIdsToRemove, setPhotosIdsToRemove] = useState([]);

  // Notes system
  const [notesSystemModalVisible, setNotesSystemModalVisible] = useState(false);
  const [notesView, setNotesView] = useState("list"); // "list" or "add"
  const [newNoteText, setNewNoteText] = useState("");
  const [newNoteAttachment, setNewNoteAttachment] = useState(null);
  const [newNoteAttachmentName, setNewNoteAttachmentName] = useState("");
  const [addNoteLoading, setAddNoteLoading] = useState(false);
  const [approvePickupDetailsModalOpened, setApprovePickupDetailsModalOpened] =
    useState(false);

  const randomString = () => {
    return Array.from({ length: 32 }, () =>
      "0123456789abcdefghijklmnopqrstuvwxyz".charAt(
        Math.floor(Math.random() * 32),
      ),
    ).join("");
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (!acceptedFiles.length) {
      setShowWrongFileTypeAlert(true);
      setTimeout(() => setShowWrongFileTypeAlert(false), 2000);
      return;
    }

    setSelectedFilesForUpload(acceptedFiles);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    useFsAccessApi: false,
    webkitdirectory: true,
    directory: true,
    multiple: true,
    accept: { "image/*": [".jpg", ".jpeg", ".png", ".tiff", ".tif"] },
  });

  // Helper to check if a file/url is a TIFF image
  const isTiffImage = (url, file) => {
    // file is defined only for newly uploaded photos (File object);
    // for photos coming from backend we rely on the URL / path.
    if (file?.type === "image/tiff") return true;
    if (file?.name?.toLowerCase().endsWith(".tiff")) return true;
    if (file?.name?.toLowerCase().endsWith(".tif")) return true;
    if (url?.toLowerCase().endsWith(".tiff")) return true;
    if (url?.toLowerCase().endsWith(".tif")) return true;
    return false;
  };

  useEffect(() => {
    if (inboundRequestId) {
      dispatch(thunks.getIncotermField(inboundRequestId));
    }
  }, [inboundRequestId]);

  useEffect(() => {
    if (inboundRequestId && incotermField) {
      if (
        userGroup === groupTypes.tomorrowCrew ||
        userGroup === groupTypes.brand ||
        userGroup === groupTypes.supplier
      )
        dispatch(thunks.getInboundRequest(inboundRequestId));
    }
  }, [inboundRequestId, incotermField]);

  // Autocomplete pickup fields
  useEffect(() => {
    if (inboundRequest) {
      setPickupConfirmationNumber(
        inboundRequest.pickupConfirmationNumber || "",
      );
      setTrackingNumber(inboundRequest.trackingNumber || "");
      // Parse pickupDate as local date to avoid timezone issues
      // When date comes as string "YYYY-MM-DD", parse it as local date at noon
      if (inboundRequest.pickupDate) {
        const parsedDate = dayjs(inboundRequest.pickupDate);
        if (parsedDate.isValid()) {
          // Create a Date object at local noon to avoid timezone/DST issues
          const localDate = new Date(
            parsedDate.year(),
            parsedDate.month(),
            parsedDate.date(),
            12,
            0,
            0,
            0,
          );
          setPickupDate(localDate);
        } else {
          setPickupDate(inboundRequest.pickupDate);
        }
      } else {
        setPickupDate("");
      }
      setPickupTime(
        inboundRequest.pickupTime
          ? dayjs(inboundRequest.pickupTime, "HH:mm:ss")
          : "",
      );
      setInvoiceNumber(inboundRequest.invoiceNumber || "");
      setCarrierName(inboundRequest.carrierName || "");
      // Parse expectedDeliveryDate as local date to avoid timezone issues
      // When date comes as string "YYYY-MM-DD", parse it as local date at noon
      if (inboundRequest.expectedDeliveryDate) {
        const parsedDate = dayjs(inboundRequest.expectedDeliveryDate);
        if (parsedDate.isValid()) {
          // Create a Date object at local noon to avoid timezone/DST issues
          const localDate = new Date(
            parsedDate.year(),
            parsedDate.month(),
            parsedDate.date(),
            12,
            0,
            0,
            0,
          );
          setExpectedDeliveryDate(localDate);
        } else {
          setExpectedDeliveryDate(inboundRequest.expectedDeliveryDate);
        }
      } else {
        setExpectedDeliveryDate("");
      }
      setDeliveryAddress(inboundRequest.deliveryAddress || "");
      setBarcodeLabellingRequired(
        inboundRequest.barcodeLabellingRequired || false,
      );
      setDeliveryNumber(inboundRequest.deliveryNumber || "");
      setTotalCartonOrPalletQTY(inboundRequest.totalCartonOrPalletQTY || 0);
      setBoxDimensions(inboundRequest.boxDimensions || "");
      setPalletDimensions(inboundRequest.palletDimensions || "");
      setWeight(inboundRequest.weight || "");
      setPickupAddress(inboundRequest.pickupAddress || "");
      setOpeningHours(inboundRequest.openingHours || "");
      setPhoneNumber(inboundRequest.phoneNumber || "");
      setSupplierEmailContact(inboundRequest.supplierEmailContact || "");
      setContentDetails(inboundRequest.contentDetails || "");
      setPackingListNumber(inboundRequest.packingListNumber || "");
      setComments(inboundRequest.comments || "");

      setWaybillFileName(inboundRequest.waybill || "");
      setCourierPhotos(inboundRequest?.photos || []);
    }
  }, [inboundRequest]);

  const handleSubmit = () => {
    const formData = new FormData();

    // Append fields
    formData.append("inboundRequestId", inboundRequestId);
    formData.append(
      "documentNumber",
      inboundRequest.purchase_order.documentNumber,
    );

    formData.append("pickupDate", moment(pickupDate).format("YYYY-MM-DD"));
    formData.append("pickupTime", pickupTime);
    formData.append(
      "expectedDeliveryDate",
      expectedDeliveryDate
        ? dayjs(expectedDeliveryDate).format("YYYY-MM-DD")
        : "",
    );
    formData.append("trackingNumber", trackingNumber);
    formData.append("pickupConfirmationNumber", pickupConfirmationNumber);
    formData.append("carrierName", carrierName);

    formData.append("waybillName", waybillFileName);

    // Append files
    if (waybillFileData instanceof File) {
      formData.append("waybill", waybillFileData);
    }

    courierPhotos.forEach((photo) => {
      if (photo.file instanceof File) {
        formData.append("photos", photo.file);
      }
    });

    // Append photos ids to be removed
    formData.append("photosIdsToRemove", photosIdsToRemove);

    dispatch(thunks.submitPickUpDetails(formData));
  };

  const handleApprovePickupDetails = () => {
    dispatch(thunks.approvePickupDetails(inboundRequestId));
  };

  const renderBackButton = () => {
    // Check if user came from IR Details page (via query parameter)
    const urlParams = new URLSearchParams(window.location.search);
    const fromDetails = urlParams.get("from") === "details";

    const editPath = window.location.href.includes("requests")
      ? paths.dashboard.inbound.requests.edit + inboundRequestId
      : paths.dashboard.inbound.asn.edit + inboundRequestId;

    const indexPath = window.location.href.includes("requests")
      ? paths.dashboard.inbound.requests.index
      : paths.dashboard.inbound.asn.index;

    return (
      <Stack
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
          padding: "20px 25px",
        }}
      >
        <Stack
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            cursor: "pointer",
            padding: 5,
          }}
          sx={{
            "&:hover": {
              textDecoration: "underline",
            },
          }}
          onClick={() =>
            (window.location.href = fromDetails ? editPath : indexPath)
          }
        >
          <KeyboardBackspace />
          <CustomTypography
            message={
              fromDetails ? "Inbound Request Details" : "Inbound Requests"
            }
            style={{ fontSize: 12 }}
          />
        </Stack>
      </Stack>
    );
  };

  const renderPickupDateAutoselect = () => {
    const noPermission =
      inboundRequest?.status !== getInboundStatusType.approved ||
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    return (
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <span
          style={{
            fontFamily: "tmwCustomFont",
            fontSize: 11,
            color: noPermission ? "lightgrey" : "black",
            letterSpacing: 3,
            fontWeight: 500,
            userSelect: "none",
            textTransform: "uppercase",
          }}
        >
          Estimated Pick-up Date *
        </span>
        <MobileDatePicker
          inputFormat="dd/MM/yyyy"
          value={pickupDate}
          onChange={(date) => {
            if (date) {
              // Normalize date to noon local time to avoid DST/timezone issues
              const normalizedDate = new Date(date);
              normalizedDate.setHours(12, 0, 0, 0);
              setPickupDate(normalizedDate);
            } else {
              setPickupDate(date);
            }
          }}
          renderInput={(inputProps) => {
            return (
              <>
                <CustomTextField
                  style={{ width: "300px" }}
                  // label={t(tokens.linesheets.table.windowStart)}w
                  // onKeyDown={onKeyDown}
                  inputProps={{
                    value: inputProps.inputProps.value,
                    onClick: inputProps.inputProps.onClick,
                  }}
                  disabled={noPermission}
                  calendar={!noPermission || undefined}
                  error={!pickupDate}
                  // touched={formik.touched.inboundDelivery?.from}
                  // errors={formik.errors.inboundDelivery?.from}
                />
              </>
            );
          }}
          disabled={noPermission}
          // maxDate={formik.values.inboundDelivery.to}
        />
      </Stack>
    );
  };

  const renderPickupTimeAutoselect = () => {
    const noPermission =
      inboundRequest?.status !== getInboundStatusType.approved ||
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    return (
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <span
          style={{
            fontFamily: "tmwCustomFont",
            fontSize: 11,
            color: noPermission ? "lightgrey" : "black",
            letterSpacing: 3,
            fontWeight: 500,
            userSelect: "none",
            textTransform: "uppercase",
          }}
        >
          Estimated Pick-up Time *
        </span>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            value={pickupTime}
            onChange={() => {}}
            onAccept={(time) => {
              setPickupTime(time);
            }}
            ampm={false}
            minutesStep={15}
            views={["hours", "minutes"]}
            renderInput={(inputProps) => (
              <CustomTextField
                style={{ width: "300px" }}
                disabled
                inputProps={inputProps}
                onKeyDown={(e) => e.preventDefault()}
              />
            )}
            disabled={noPermission}
          />
        </LocalizationProvider>
      </Stack>
    );
  };

  const renderInvoiceNumber = () => {
    const noPermission =
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    return (
      <Stack>
        <CustomTextField
          label="Invoice number"
          style={{ width: "300px" }}
          labelStyle={{
            color: noPermission ? "lightgrey" : "black",
          }}
          value={invoiceNumber || ""}
          handleChange={(e) => setInvoiceNumber(e.target.value)}
          disabled={noPermission}
        />
      </Stack>
    );
  };

  const renderCarrierName = () => {
    const noPermission =
      inboundRequest?.status !== getInboundStatusType.approved ||
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    return (
      <Stack>
        <CustomTextField
          label="Carrier Name *"
          style={{ width: "300px" }}
          labelStyle={{
            color: noPermission ? "lightgrey" : "black",
          }}
          value={carrierName || ""}
          handleChange={(e) => setCarrierName(e.target.value)}
          disabled={noPermission}
          error={!carrierName}
        />
      </Stack>
    );
  };

  const renderExpectedDeliveryDateAutoselect = () => {
    const noPermission =
      inboundRequest?.status !== getInboundStatusType.approved ||
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    return (
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <span
          style={{
            fontFamily: "tmwCustomFont",
            fontSize: 11,
            color: noPermission ? "lightgrey" : "black",
            letterSpacing: 3,
            fontWeight: 500,
            userSelect: "none",
            textTransform: "uppercase",
          }}
        >
          Expected Delivery Date *
        </span>
        <MobileDatePicker
          inputFormat="dd/MM/yyyy"
          value={expectedDeliveryDate}
          onChange={(date) => {
            if (date) {
              // Normalize date to noon local time to avoid DST/timezone issues
              const normalizedDate = new Date(date);
              normalizedDate.setHours(12, 0, 0, 0);
              setExpectedDeliveryDate(normalizedDate);
            } else {
              setExpectedDeliveryDate(date);
            }
          }}
          renderInput={(inputProps) => {
            return (
              <>
                <CustomTextField
                  style={{ width: "300px" }}
                  // label={t(tokens.linesheets.table.windowStart)}
                  // onKeyDown={onKeyDown}
                  inputProps={{
                    value: inputProps.inputProps.value,
                    onClick: inputProps.inputProps.onClick,
                  }}
                  disabled={noPermission}
                  calendar={!noPermission || undefined}
                  // touched={formik.touched.inboundDelivery?.from}
                  // errors={formik.errors.inboundDelivery?.from}
                />
              </>
            );
          }}
          disabled={noPermission}
          error={!expectedDeliveryDate}
          // maxDate={formik.values.inboundDelivery.to}
        />
      </Stack>
    );
  };

  const renderDeliveryAddress = () => {
    const noPermission =
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    return (
      <Stack>
        <CustomTextField
          label="Delivery Address (Tomorrow Warehouse Location)"
          style={{ width: "300px", marginTop: -17 }}
          labelStyle={{
            color: noPermission ? "lightgrey" : "black",
          }}
          value={deliveryAddress || ""}
          handleChange={(e) => setDeliveryAddress(e.target.value)}
          disabled={noPermission}
          multiline
          minRows={2}
        />
      </Stack>
    );
  };

  const renderBarcodeLabellingRequired = () => {
    const noPermission =
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    return (
      <CustomFormControl
        style={{ margin: "13px 0" }}
        control={
          <Checkbox style={{ color: noPermission ? "grey" : "black" }} />
        }
        label={
          <CustomTypography
            style={{ userSelect: "none" }}
            message="Barcode labelling required"
          />
        }
        checked={barcodeLabellingRequired}
        onChange={(e) => setBarcodeLabellingRequired(e.target.checked)}
        disabled={noPermission}
      />
    );
  };

  const renderDeliveryNumber = () => {
    const noPermission =
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    return (
      <Stack>
        <CustomTextField
          label="Delivery number"
          style={{ width: "300px" }}
          labelStyle={{
            color: noPermission ? "lightgrey" : "black",
          }}
          value={deliveryNumber || ""}
          handleChange={(e) => setDeliveryNumber(e.target.value)}
          disabled={noPermission}
        />
      </Stack>
    );
  };

  const renderTotalCartonOrPalletQTY = () => {
    const noPermission =
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    return (
      <Stack>
        <CustomTextField
          label="Total Carton or Pallet QTY"
          type="number"
          style={{ width: "300px" }}
          labelStyle={{
            color: noPermission ? "lightgrey" : "black",
          }}
          value={totalCartonOrPalletQTY || 0}
          handleChange={(e) =>
            e.target.value >= 0 && setTotalCartonOrPalletQTY(e.target.value)
          }
          disabled={noPermission}
        />
      </Stack>
    );
  };

  const renderBoxDimensions = () => {
    const noPermission =
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    return (
      <Stack>
        <CustomTextField
          label="Box Dimensions (CM or INCH)"
          style={{ width: "300px" }}
          labelStyle={{
            color: noPermission ? "lightgrey" : "black",
          }}
          value={boxDimensions || ""}
          handleChange={(e) => setBoxDimensions(e.target.value)}
          disabled={noPermission}
        />
      </Stack>
    );
  };

  const renderPalletDimensions = () => {
    const noPermission =
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    return (
      <Stack>
        <CustomTextField
          label="If pallet, pallet dimensions"
          style={{ width: "300px" }}
          labelStyle={{
            color: noPermission ? "lightgrey" : "black",
          }}
          value={palletDimensions || ""}
          handleChange={(e) => setPalletDimensions(e.target.value)}
          disabled={noPermission}
        />
      </Stack>
    );
  };

  const renderWeight = () => {
    const noPermission =
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    return (
      <Stack>
        <CustomTextField
          label="Weight (KG or LB)"
          style={{ width: "300px" }}
          labelStyle={{
            color: noPermission ? "lightgrey" : "black",
          }}
          value={weight || ""}
          handleChange={(e) => setWeight(e.target.value)}
          disabled={noPermission}
        />
      </Stack>
    );
  };

  const renderPickupAddress = () => {
    const noPermission =
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    return (
      <Stack>
        <CustomTextField
          label="Pick-up Address"
          style={{ width: "300px" }}
          labelStyle={{
            color: noPermission ? "lightgrey" : "black",
          }}
          value={pickupAddress || ""}
          handleChange={(e) => setPickupAddress(e.target.value)}
          disabled={noPermission}
          multiline
          minRows={2}
        />
      </Stack>
    );
  };

  const renderOpeningHours = () => {
    const noPermission =
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    return (
      <Stack>
        <CustomTextField
          label="Opening hours"
          style={{ width: "300px" }}
          labelStyle={{
            color: noPermission ? "lightgrey" : "black",
          }}
          value={openingHours || ""}
          handleChange={(e) => setOpeningHours(e.target.value)}
          disabled={noPermission}
          multiline
          minRows={2}
        />
      </Stack>
    );
  };

  const renderPhoneNumber = () => {
    const noPermission =
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    return (
      <Stack>
        <CustomTextField
          label="Phone Number"
          style={{ width: "300px" }}
          labelStyle={{
            color: noPermission ? "lightgrey" : "black",
          }}
          value={phoneNumber || ""}
          handleChange={(e) => setPhoneNumber(e.target.value)}
          disabled={noPermission}
          multiline
          minRows={2}
        />
      </Stack>
    );
  };

  const renderSupplierEmailContact = () => {
    const noPermission =
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    return (
      <Stack>
        <CustomTextField
          label="Supplier Email Contact"
          style={{ width: "300px" }}
          labelStyle={{
            color: noPermission ? "lightgrey" : "black",
          }}
          value={supplierEmailContact || ""}
          handleChange={(e) => setSupplierEmailContact(e.target.value)}
          disabled={noPermission}
          multiline
          minRows={2}
        />
      </Stack>
    );
  };

  const renderContentDetails = () => {
    const noPermission =
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    return (
      <Stack>
        <CustomTextField
          label="Content details"
          style={{ width: "300px" }}
          labelStyle={{
            color: noPermission ? "lightgrey" : "black",
          }}
          value={contentDetails || ""}
          handleChange={(e) => setContentDetails(e.target.value)}
          disabled={noPermission}
          multiline
          minRows={2}
        />
      </Stack>
    );
  };

  const renderPackingListNumber = () => {
    const noPermission =
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    return (
      <Stack>
        <CustomTextField
          label="Packing List Number"
          style={{ width: "300px" }}
          labelStyle={{
            color: noPermission ? "lightgrey" : "black",
          }}
          value={packingListNumber || ""}
          handleChange={(e) => setPackingListNumber(e.target.value)}
          disabled={noPermission}
        />
      </Stack>
    );
  };

  const renderComments = () => {
    const noPermission =
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    return (
      <Stack>
        <CustomTextField
          label="Comments"
          style={{ width: "300px" }}
          labelStyle={{
            color: noPermission ? "lightgrey" : "black",
          }}
          value={comments || ""}
          handleChange={(e) => setComments(e.target.value)}
          disabled={noPermission}
          multiline
          minRows={2}
        />
      </Stack>
    );
  };

  const renderInputSelect = (
    inputName,
    fileName,
    setFileName,
    fileData,
    setFileData,
    allowedFiles,
    downloadLink = null,
  ) => {
    const handleFileChange = (event) => {
      const file = event.target.files[0];

      if (file) {
        const fileExtension = file.name.split(".").pop().toLowerCase();

        if (allowedFiles.includes(fileExtension)) {
          setFileName(file.name);
          setFileData(file);
        } else {
          alert("Invalid file type! Please upload a PDF file.");

          event.target.value = "";
          setFileName("");
          setFileData(null);
        }
      }
    };

    const FileNameDisplay = (fileName, downloadLink) => {
      const maxLength = 50;

      const truncatedName =
        fileName?.length > maxLength
          ? fileName.slice(0, maxLength) + "..."
          : fileName;

      if (downloadLink) {
        return (
          <span
            style={{
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => {
              window.open(downloadLink, "_blank");
            }}
          >
            {truncatedName}
          </span>
        );
      }

      return <span>{truncatedName}</span>;
    };

    const noPermission =
      inboundRequest?.status !== getInboundStatusType.approved ||
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    return (
      <Stack style={{ alignItems: "flex-start" }}>
        <CustomButton
          component="label"
          disabled={!!(fileName || fileData) || noPermission}
          label={
            <>
              {inputName}
              <Input
                type="file"
                id={inputName}
                accept={allowedFiles.map((ext) => `.${ext}`).join(", ")}
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
            </>
          }
          style={{ width: "fit-content" }}
        />
        {fileName && (
          <Stack
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 5,
              margin: "10px",
              fontFamily: "tmwCustomFont",
              fontSize: 12,
            }}
          >
            {FileNameDisplay(fileName, downloadLink)}
            {!noPermission && (
              <ButtonBase
                onClick={() => {
                  setFileName("");
                  setFileData(null);

                  // Reset the file input on close
                  const fileInput = document.getElementById(inputName);
                  if (fileInput) {
                    fileInput.value = "";
                  }
                }}
              >
                <CloseOutlined style={{ width: "18px" }} />
              </ButtonBase>
            )}
          </Stack>
        )}
      </Stack>
    );
  };

  const renderPickUpConfirmationNumber = () => {
    const noPermission =
      inboundRequest?.status !== getInboundStatusType.approved ||
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    return (
      <Stack>
        <CustomTextField
          label="Pick-up Confirmation Number"
          style={{ width: "300px" }}
          labelStyle={{
            color: noPermission ? "lightgrey" : "black",
          }}
          value={pickupConfirmationNumber || ""}
          handleChange={(e) => setPickupConfirmationNumber(e.target.value)}
          disabled={noPermission}
        />
      </Stack>
    );
  };

  const renderTrackingNumber = () => {
    const noPermission =
      inboundRequest?.status !== getInboundStatusType.approved ||
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest.incoterm === "DDP") ||
      ((userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest.incoterm === "EXW");

    const isDDP = inboundRequest?.incoterm === "DDP";
    const isRequired = isDDP;

    return (
      <Stack>
        <CustomTextField
          label={isRequired ? "Tracking Number *" : "Tracking Number"}
          style={{ width: "300px" }}
          labelStyle={{
            color: noPermission ? "lightgrey" : "black",
          }}
          value={trackingNumber || ""}
          handleChange={(e) => setTrackingNumber(e.target.value)}
          disabled={noPermission}
          error={isRequired && !trackingNumber}
        />
      </Stack>
    );
  };

  const renderPickUpDate = () => {
    return <Stack>{renderPickupDateAutoselect()}</Stack>;
  };

  const renderPickUpTime = () => {
    return <Stack>{renderPickupTimeAutoselect()}</Stack>;
  };

  const renderWaybill = () => {
    const waybillDownloadLink = inboundRequest?.waybillDownloadLink || null;
    const isSupplierOrBrand =
      userGroup === groupTypes.supplier || userGroup === groupTypes.brand;
    const hasWaybillData = waybillFileName || waybillFileData;

    // For supplier and brand users, if waybill data exists, display it in readonly mode
    if (isSupplierOrBrand && hasWaybillData && !waybillFileData) {
      const maxLength = 50;
      const truncatedName =
        waybillFileName?.length > maxLength
          ? waybillFileName.slice(0, maxLength) + "..."
          : waybillFileName;

      return (
        <Stack style={{ alignItems: "flex-start" }}>
          <CustomTypography
            message="Waybill:"
            style={{ fontSize: 12, marginBottom: "10px" }}
          />
          {waybillFileName && (
            <Stack
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 5,
                margin: "0 10px",
                fontFamily: "tmwCustomFont",
                fontSize: 12,
              }}
            >
              {waybillDownloadLink ? (
                <span
                  style={{
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                  onClick={() => {
                    window.open(waybillDownloadLink, "_blank");
                  }}
                >
                  {truncatedName}
                </span>
              ) : (
                <span>{truncatedName}</span>
              )}
            </Stack>
          )}
        </Stack>
      );
    }

    return (
      <Stack>
        {renderInputSelect(
          waybillFileData ? "Waybill" : "Upload Waybill",
          waybillFileName,
          setWaybillFileName,
          waybillFileData,
          setWaybillFileData,
          ["pdf"],
          waybillDownloadLink,
        )}
      </Stack>
    );
  };

  const renderFormDataFiles = (formData) => {
    const files = [];

    // Iterate over FormData entries
    formData.forEach((value, key) => {
      if (value && value.name && value.size) {
        files.push(value.name);
      }
    });

    return files.map((file, index) => (
      <Stack key={`file-nr-${index}`}>{file}</Stack>
    ));
  };

  const renderDropzone = (getRootProps, getInputProps, isDragActive) => {
    return (
      <Stack>
        <Stack
          p={5}
          {...getRootProps()}
          style={{
            width: "100%",
            height: "300px",
            border: "1px dashed lightgrey",
            fontFamily: "tmwCustomFont",
            textTransform: "uppercase",
            fontSize: 12,
            textAlign: "center",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <input {...getInputProps({ multiple: true })} />
          {!showWrongFileTypeAlert ? (
            isDragActive ? (
              <Stack
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "lightgrey",
                }}
              >
                <CloudUploadIcon style={{ height: "40px", width: "40px" }} />
                <Stack style={{ fontSize: 10 }}>
                  Click / Drag and drop to add folder or files
                </Stack>
              </Stack>
            ) : (
              <Stack
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "black",
                }}
              >
                <CloudUploadIcon style={{ height: "40px", width: "40px" }} />
                <Stack style={{ fontSize: 10 }}>
                  Click / Drag and drop to attach photos
                </Stack>
              </Stack>
            )
          ) : (
            <Stack
              style={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "red",
              }}
            >
              Choose a valid input
            </Stack>
          )}
        </Stack>
        {selectedFilesForUpload && (
          <Stack
            style={{
              alignItems: "center",
              justifyContent: "flex-start",
              fontSize: 12,
              margin: "15px 10px",
              width: "100%",
              gap: 5,
            }}
          >
            <CustomTypography message="Selected files:" />
            <Stack>{renderFormDataFiles(selectedFilesForUpload)}</Stack>
          </Stack>
        )}
      </Stack>
    );
  };

  const handleUploadFiles = async () => {
    if (selectedFilesForUpload) {
      const tempArray = [...courierPhotos];
      selectedFilesForUpload.map((selectedFileForUpload) => {
        tempArray.push({
          id: randomString(),
          url: "",
          file: selectedFileForUpload,
        });
      });
      console.log(tempArray);
      setCourierPhotos(tempArray);
      setSelectedFilesForUpload(null);
      setAddItemModalVisible(false);
    }
  };

  const renderAddItemModal = () => {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100vh",
          backdropFilter: "blur(5px)",
          zIndex: 1000,
        }}
      >
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1100,
            minWidth: "700px",
          }}
        >
          <Container>
            <Stack
              elevation={12}
              style={{
                padding: "25px 25px 50px 25px",
                background: "white",
                border: "1px solid #E6E6E6",
              }}
            >
              <Stack style={{ gap: 25 }}>
                <Stack
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 5,
                  }}
                >
                  <CustomTypography title message="Upload Photos" />
                </Stack>
                {renderDropzone(getRootProps, getInputProps, isDragActive)}
              </Stack>
              <Stack
                style={{
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  gap: 10,
                  margin: "35px 0 -15px 0",
                }}
              >
                <CustomButton
                  label="Cancel"
                  smallButton
                  onClick={() => {
                    setSelectedFilesForUpload(null);
                    setAddItemModalVisible(false);
                  }}
                />
                <CustomButton
                  label="Upload"
                  smallButton
                  greenButton
                  onClick={() => handleUploadFiles()}
                  disabled={!selectedFilesForUpload}
                />
              </Stack>
            </Stack>
          </Container>
        </Box>
      </div>
    );
  };

  const renderPhotosSection = () => {
    const noPermission =
      inboundRequest?.status !== getInboundStatusType.approved ||
      userGroup === groupTypes.tomorrowCrew;

    const removePhoto = (idToRemove) => {
      setPhotosIdsToRemove([...photosIdsToRemove, idToRemove]);
      setCourierPhotos((prevPhotos) =>
        prevPhotos.filter((photo) => photo.id !== idToRemove),
      );
    };

    return (
      <>
        <Stack
          style={{
            alignItems: "flex-start",
            width: "100%",
            margin: "20px 0 10px 25px",
          }}
        >
          <CustomTypography title message="Photos" />
        </Stack>
        {
          <Stack
            style={{
              width: "100%",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
              gap: "10px",
              padding: "10px",
              border: "1px solid #E6E6E6",
              justifyItems: "center",
            }}
          >
            {courierPhotos.map((photo, index) => {
              const photoKey = `courier_photo${index}`;

              // Support both shapes:
              // - backend photos: string path / URL
              // - local photos: { id, url, file }
              const photoUrl =
                typeof photo === "string" ? photo : photo?.url || "";
              const photoFile =
                typeof photo === "object" && photo !== null && "file" in photo
                  ? photo.file
                  : undefined;

              // Build a URL for the image if possible:
              // - backend photos: use the URL/path directly
              // - local uploads: use a blob URL from the File
              const imageUrl = photoUrl
                ? photoUrl
                : photoFile
                  ? URL.createObjectURL(photoFile)
                  : "";

              // Detect TIFF (browsers generally can't render TIFF images)
              const isTiff = isTiffImage(photoUrl, photoFile);
              const canShowThumbnail = !!imageUrl && !isTiff;

              const linkLabel =
                (typeof photo === "object" && photo !== null && photo.name) ||
                photoFile?.name ||
                (photoUrl ? photoUrl.split("/").pop() : `Photo ${index + 1}`);

              return (
                <Stack
                  key={photoKey}
                  style={{
                    position: "relative",
                    cursor: "pointer",
                  }}
                  onMouseEnter={() => setHoveredPhoto(photoKey)}
                  onMouseLeave={() => setHoveredPhoto("")}
                >
                  <a
                    href={imageUrl || "#"}
                    target={imageUrl ? "_blank" : "_self"}
                    rel={imageUrl ? "noopener noreferrer" : undefined}
                  >
                    {canShowThumbnail ? (
                      <img
                        src={imageUrl}
                        alt={`Photo ${index + 1}`}
                        style={{
                          height: "100px",
                          width: "100px",
                          objectFit: "cover",
                          opacity: hoveredPhoto === photoKey ? "60%" : "100%",
                          transition: "ease-in-out 0.1s",
                        }}
                      />
                    ) : (
                      <span
                        style={{
                          fontSize: 12,
                          textDecoration: "underline",
                          color: "#1976d2",
                          wordBreak: "break-all",
                        }}
                      >
                        {linkLabel}
                      </span>
                    )}
                  </a>

                  {hoveredPhoto === photoKey && !noPermission && (
                    <CustomTooltip title="Remove photo">
                      <Delete
                        style={{
                          position: "absolute",
                          right: 0,
                          color: "red",
                          background: "white",
                          width: "20px",
                          height: "20px",
                        }}
                        onClick={() => removePhoto(photo.id)}
                      />
                    </CustomTooltip>
                  )}
                </Stack>
              );
            })}

            {!noPermission && (
              <CustomTooltip title="Add Photos">
                <div
                  key="add-photo"
                  style={{
                    height: "100px",
                    width: "100px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed #E6E6E6",
                    fontSize: 28,
                    color: hoveredPhoto === "add-photo" ? "white" : "#E6E6E6",
                    background:
                      hoveredPhoto === "add-photo" ? "#E6E6E6" : "white",
                    cursor: "pointer",
                    transition: "ease-in-out 0.15s",
                  }}
                  onClick={() => {
                    setAddItemModalVisible(true);
                  }}
                  onMouseEnter={() => setHoveredPhoto("add-photo")}
                  onMouseLeave={() => setHoveredPhoto("")}
                >
                  +
                </div>
              </CustomTooltip>
            )}
          </Stack>
        }
      </>
    );
  };

  const renderApprovePickupDetailsModal = (
    handleConfirm,
    handleCancel,
    message,
    submitLoader,
  ) => {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backdropFilter: "blur(5px)",
          zIndex: 1100,
        }}
      >
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1100,
            width: "50vw",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Container maxWidth="sx">
            <Stack
              elevation={12}
              style={{
                background: "white",
                borderRadius: "25px",
                border: "1px solid #E6E6E6",
              }}
            >
              <Stack
                style={{
                  height: "40px",
                  background: "green",
                  borderRadius: "25px 25px 0 0",
                }}
              />
              <Stack style={{ padding: "25px", gap: 20 }}>
                <Stack style={{ padding: "35px", gap: 15 }}>
                  {message && <CustomTypography message={message} />}
                </Stack>
                <Stack
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    gap: 15,
                    marginTop: "15px",
                  }}
                >
                  <CustomButton
                    label="Cancel"
                    onClick={handleCancel}
                    disabled={submitLoader}
                  />
                  <CustomButton
                    label="Confirm"
                    onClick={handleConfirm}
                    disabled={submitLoader}
                    loader={
                      submitLoader && (
                        <CircularProgress
                          size={20}
                          color="inherit"
                          style={{ marginLeft: "10px" }}
                        />
                      )
                    }
                  />
                </Stack>
              </Stack>
            </Stack>
          </Container>
        </Box>
      </div>
    );
  };

  const renderCourierDetailsSection = () => {
    const renderDivider = (
      <div
        style={{
          width: "100%",
          border: "1px dashed #E6E6E6",
        }}
      />
    );

    const noPermission =
      inboundRequest?.status !== getInboundStatusType.approved ||
      userGroup === groupTypes.tomorrowCrew;

    return (
      <Stack
        style={{
          display: "flex",
          flexDirection: "row",
          gap: 30,
          border: "1px solid #E6E6E6",
          width: "90vw",
          padding: "30px 30px 40px 30px",
          margin: "25px 25px 100px 25px",
        }}
      >
        <Stack style={{ width: "100%", gap: 40 }}>
          <Stack
            style={{
              width: "100%",
              gap: 50,
            }}
          >
            <Stack
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 25,
              }}
            >
              {renderPickUpDate()}
              {renderPickUpTime()}
              {renderExpectedDeliveryDateAutoselect()}
              {renderTrackingNumber()}
              {renderPickUpConfirmationNumber()}
              {renderWaybill()}
              {renderCarrierName()}
            </Stack>
          </Stack>
          <Stack
            style={{
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {!!(!noPermission || courierPhotos.length) && renderPhotosSection()}
          </Stack>
        </Stack>
      </Stack>
    );
  };

  const renderPermissionInfo = (groupType) => {
    return (
      <Stack
        style={{
          display: "flex",
          flexDirection: "row",
          gap: 30,
          minWidth: "800px",
          padding: "10px",
          justifyContent: "center",
        }}
      >
        <CustomTypography
          message={
            inboundRequest?.status === getInboundStatusType.approved &&
            (userGroup === groupTypes.tomorrowCrew
              ? `(This info can only be updated by ${groupType})`
              : "(You can only update the photos section)")
          }
          style={{ fontStyle: "italic", fontWeight: "bold" }}
        />
      </Stack>
    );
  };

  const renderApprovePickupDetailsButton = () => {
    return (
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest?.courierInfoCompleted &&
      courierPhotos.length > 0 &&
      pickupDate &&
      pickupTime &&
      expectedDeliveryDate &&
      carrierName && (
        <CustomButton
          style={{
            position: "fixed",
            bottom: 40,
            right: 175,
            width: "125px",
            background: "white",
          }}
          label="Approve"
          onClick={() => setApprovePickupDetailsModalOpened(true)}
        />
      )
    );
  };

  const renderSubmitButton = () => {
    const submitButtonDisabled =
      (!(courierPhotos.length > 0) &&
        (userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest?.incoterm === "EXW") ||
      (!(
        trackingNumber &&
        pickupDate &&
        pickupTime &&
        carrierName &&
        expectedDeliveryDate &&
        courierPhotos.length > 0
      ) &&
        (userGroup === groupTypes.brand || userGroup === groupTypes.supplier) &&
        inboundRequest?.incoterm === "DDP") ||
      (userGroup === groupTypes.tomorrowCrew &&
        inboundRequest?.incoterm === "DDP") ||
      (!(pickupDate && pickupTime && carrierName && expectedDeliveryDate) &&
        userGroup === groupTypes.tomorrowCrew &&
        inboundRequest?.incoterm === "EXW");

    return (
      <CustomButton
        style={{
          position: "fixed",
          bottom: 40,
          right: 25,
          width: "125px",
          background: "white",
        }}
        label="Submit"
        disabled={submitButtonDisabled}
        onClick={handleSubmit}
      />
    );
  };

  const renderNotesSystemModal = () => {
    if (!inboundRequest) return null;

    const handleAddNote = async () => {
      if (!newNoteText.trim() && !newNoteAttachment) {
        toast.error("Please add either text or attachment");
        return;
      }

      setAddNoteLoading(true);
      try {
        const formData = new FormData();
        formData.append("noteId", `${Date.now()}-${Math.random()}`);
        formData.append("inboundRequestId", inboundRequest.id);
        formData.append("note", newNoteText);
        if (newNoteAttachment) {
          formData.append("attachment", newNoteAttachment);
        }

        await dispatch(thunks.addInboundRequestNote(formData));

        setNewNoteText("");
        setNewNoteAttachment(null);
        setNewNoteAttachmentName("");

        // Refresh inbound request notes
        dispatch(thunks.getInboundRequest(inboundRequest.id));

        toast.success("Note added successfully");
      } catch (error) {
        toast.error("Failed to add note");
      } finally {
        setAddNoteLoading(false);
      }
    };

    const sortedNotes = [...(inboundRequest.notes || [])].sort(
      (a, b) => new Date(b.date) - new Date(a.date),
    );

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backdropFilter: "blur(5px)",
          zIndex: 1100,
        }}
      >
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1100,
            width: "80vw",
            maxHeight: "90vh",
          }}
        >
          <Container maxWidth="sx">
            <Stack
              elevation={12}
              style={{
                background: "white",
                borderRadius: "25px",
                border: "1px solid #E6E6E6",
                maxHeight: "90vh",
                overflow: "hidden",
              }}
            >
              <Stack
                style={{
                  height: "40px",
                  background: "#333",
                  borderRadius: "25px 25px 0 0",
                }}
              />
              <Stack
                style={{
                  padding: "25px",
                  gap: 20,
                  maxHeight: "calc(90vh - 40px)",
                  overflow: "hidden",
                }}
              >
                <Stack
                  style={{
                    width: "100%",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <CustomTypography
                    message={
                      notesView === "list"
                        ? "Inbound Request Notes"
                        : "Add New Note"
                    }
                    title
                    style={{ marginBottom: "10px" }}
                  />
                </Stack>

                {/* Notes list */}
                {notesView === "list" && (
                  <Stack
                    style={{
                      flex: 1,
                      overflow: "auto",
                      border: "1px solid #E6E6E6",
                    }}
                  >
                    <Stack
                      style={{
                        padding: "15px",
                        borderBottom: "1px solid #E6E6E6",
                        background: "#f8f8f8",
                      }}
                    >
                      <CustomTypography
                        message={`Notes (${sortedNotes.length})`}
                        style={{ fontSize: 14, fontWeight: 600 }}
                      />
                    </Stack>
                    <Stack style={{ maxHeight: "400px", overflow: "auto" }}>
                      {sortedNotes.length === 0 ? (
                        <Stack
                          style={{
                            padding: "40px",
                            textAlign: "center",
                            color: "#666",
                          }}
                        >
                          <CustomTypography message="No notes yet" />
                        </Stack>
                      ) : (
                        sortedNotes.map((note, index) => (
                          <Stack
                            key={index}
                            style={{
                              padding: "15px",
                              borderBottom:
                                index !== sortedNotes.length - 1
                                  ? "1px solid #E6E6E6"
                                  : "none",
                              gap: 8,
                            }}
                          >
                            <Stack
                              style={{
                                flexDirection: "row",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                              }}
                            >
                              <Stack style={{ gap: 5 }}>
                                <Stack
                                  style={{
                                    flexDirection: "row",
                                    gap: 5,
                                    alignItems: "center",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 12,
                                      color: "#666",
                                      fontFamily: "tmwCustomFont",
                                    }}
                                  >
                                    {moment(note.date).format(
                                      "DD/MM/YYYY HH:mm",
                                    )}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 12,
                                      color: "#666",
                                      fontFamily: "tmwCustomFont",
                                    }}
                                  >
                                    by {note.addedBy || "Unknown"}
                                  </span>
                                </Stack>
                                {note.note && (
                                  <Stack
                                    style={{
                                      fontFamily: "tmwCustomFont",
                                      fontSize: 14,
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    {note.note}
                                  </Stack>
                                )}
                              </Stack>
                            </Stack>
                          </Stack>
                        ))
                      )}
                    </Stack>
                  </Stack>
                )}

                {/* Add note view */}
                {notesView === "add" && (
                  <Stack
                    style={{
                      border: "1px solid #E6E6E6",
                      padding: "20px",
                      gap: 15,
                    }}
                  >
                    <Stack style={{ gap: 10 }}>
                      <CustomTextField
                        label="Note Text (optional)"
                        value={newNoteText}
                        handleChange={(e) => setNewNoteText(e.target.value)}
                        multiline
                        minRows={3}
                        maxRows={6}
                      />
                      <Stack
                        style={{
                          flexDirection: "row",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >
                        <CustomButton
                          component="label"
                          label={
                            <>
                              Upload Attachment
                              <Input
                                type="file"
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    setNewNoteAttachment(file);
                                    setNewNoteAttachmentName(file.name);
                                  }
                                }}
                                style={{ display: "none" }}
                              />
                            </>
                          }
                          style={{ width: "fit-content" }}
                        />
                        <CustomTypography
                          message="(optional)"
                          style={{ fontSize: 12 }}
                        />
                        {newNoteAttachmentName && (
                          <Stack
                            style={{
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 5,
                            }}
                          >
                            <span style={{ fontSize: 12, color: "#666" }}>
                              {newNoteAttachmentName}
                            </span>
                            <ButtonBase
                              onClick={() => {
                                setNewNoteAttachment(null);
                                setNewNoteAttachmentName("");
                              }}
                            >
                              <CloseOutlined style={{ width: "16px" }} />
                            </ButtonBase>
                          </Stack>
                        )}
                      </Stack>
                      <Stack
                        style={{
                          flexDirection: "row",
                          justifyContent: "flex-end",
                        }}
                      >
                        <CustomButton
                          label="Insert Note"
                          onClick={handleAddNote}
                          disabled={
                            addNoteLoading ||
                            (!newNoteText.trim() && !newNoteAttachment)
                          }
                          loader={
                            addNoteLoading && (
                              <CircularProgress
                                size={20}
                                color="inherit"
                                style={{ marginLeft: "10px" }}
                              />
                            )
                          }
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                )}

                <Stack
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    gap: 15,
                    margin: "0 25px 0 0",
                  }}
                >
                  {notesView === "list" ? (
                    <CustomButton
                      label="Add New Note"
                      onClick={() => setNotesView("add")}
                      style={{ background: "white" }}
                    />
                  ) : (
                    <CustomButton
                      label="Back to Notes"
                      onClick={() => setNotesView("list")}
                      style={{ background: "white" }}
                    />
                  )}
                  <CustomButton
                    label="Close"
                    onClick={() => {
                      setNotesSystemModalVisible(false);
                      setNotesView("list");
                    }}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Container>
        </Box>
      </div>
    );
  };

  return (
    <>
      <Seo title="Dashboard: Edit Courier Info" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: 3,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {renderBackButton()}
          {inboundRequest && (
            <CustomButton
              label="Notes"
              style={{
                width: "125px",
                background: "white",
                marginRight: 5,
                marginTop: 2,
              }}
              onClick={() => setNotesSystemModalVisible(true)}
            />
          )}
        </Stack>
        {!incotermFieldLoading ? (
          userGroup === groupTypes.tomorrowCrew ||
          userGroup === groupTypes.brand ||
          userGroup === groupTypes.supplier ? (
            <Stack
              style={{
                height: "90%",
                width: "100%",
                alignItems: "center",
                marginTop: "-25px",
              }}
            >
              {!inboundRequestLoading &&
                (inboundRequest?.status === getInboundStatusType.approved ||
                inboundRequest?.status === getInboundStatusType.inTransit ||
                inboundRequest?.status === getInboundStatusType.received ||
                inboundRequest?.status ===
                  getInboundStatusType.partiallyReceived ||
                inboundRequest?.status === getInboundStatusType.closed ? (
                  <Stack
                    style={{
                      height: "60%",
                      justifyContent: "space-between",
                    }}
                  >
                    <Stack
                      style={{
                        marginBottom: "10px",
                        width: "100%",
                        alignItems: "center",
                      }}
                    >
                      <CustomTypography message="Pick-up Details" title />
                    </Stack>
                    {userGroup === groupTypes.tomorrowCrew &&
                      incotermField === "DDP" &&
                      renderPermissionInfo("Brands")}
                    {(userGroup === groupTypes.brand ||
                      userGroup === groupTypes.supplier) &&
                      incotermField === "EXW" &&
                      renderPermissionInfo("Tomorrow")}
                    {renderCourierDetailsSection()}
                  </Stack>
                ) : (
                  <Stack style={{ height: "75%", justifyContent: "center" }}>
                    <CustomTypography
                      message="Pick-up details not available for this inbound request"
                      title
                    />
                  </Stack>
                ))}
              {inboundRequest?.status === getInboundStatusType.approved &&
                renderApprovePickupDetailsButton()}
              {inboundRequest?.status === getInboundStatusType.approved &&
                renderSubmitButton()}
              {addItemModalVisible && renderAddItemModal()}
              {approvePickupDetailsModalOpened &&
                userGroup === groupTypes.tomorrowCrew &&
                renderApprovePickupDetailsModal(
                  () => handleApprovePickupDetails(),
                  () => setApprovePickupDetailsModalOpened(false),
                  "Are you sure you want to approve the pickup details? This action will also Mark In Transit the Inbound Shipment on NetSuite.",
                  inboundRequestStatusChangeApproveLoading,
                )}
              {notesSystemModalVisible && renderNotesSystemModal()}
            </Stack>
          ) : (
            <Error404Page />
          )
        ) : (
          <Stack
            style={{
              width: "100%",
              height: "75%",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={35} color="inherit" />
          </Stack>
        )}
      </Box>
    </>
  );
};

export default withCourierInboundProcessGuard(EditCourierDetailsPage);
